import { graphql } from "gatsby"
import React from "react"
import ReactMarkdown from "react-markdown"
import NewLayout from "../components/new_layout"
import Card from "../components/v2023/UI/Card/card"
import rehypeRaw from "rehype-raw"
import SEO from "../components/seo"
import { MainSection100 } from '../components/Layout/sections'
import { Container } from '../components/Layout/containers'
import { Tile } from '../components/v2024/tile/tile'
import { Tabs } from '../components/v2024/tabs'
import slugify from "slugify"

// Reusable grid component:
const CompetitorGrid = ({
  type,                       // Firma, verificacion, ambas
  competitors,               // array de competidores (filtrados)
  lang,
  pathComparativeCompetitors // { en: "/alternatives", es: "/es/alternativas" }
}) => {

  // Determinamos el query string según la categoría
  const getQueryString = () => {
    if (type === "firma") return "?firma_electronica"
    if (type === "verificacion") return "?verificacion_identidad"
    // "ambas": no agrega query
    return ""
  }

  return (
    <div className="tabs-content">
      <div className="grid-lg-3">
        {competitors.map(comp => {
          // Ej: "/alternatives/competitor-name"
          const baseUrl = pathComparativeCompetitors[lang] + "/" + slugify(comp.url) 
          // Concatenamos el query string
          const competitorUrl = baseUrl + getQueryString()

          return (
            <Card
              key={comp.id}
              name={comp.competitor_comparative}
              description={comp.seo.meta_description}
              showMore
              lang={lang}
              url={competitorUrl}
            />
          )
        })}
      </div>
    </div>
  )
}

const AlternativasCompentenciaPage = ({ data, pageContext }) => {
  const page = data.page.edges[0]?.node
  const lang = pageContext.langKey

  // Rutas base donde se encuentran los detalles de cada competidor
  const pathComparativeCompetitors = {
    en: "/alternatives",
    es: "/es/alternativas"
  }

  // Array con todos los competidores
  const competidores = data.competitives.edges.map(edge => edge.node)

  // Filtrados para cada tipo de pestaña
  const competitorsFirma = competidores.filter(
    comp => comp.comparative_electronic_signature_button?.trim().length > 0
  )
  const competitorsVerificacion = competidores.filter(
    comp => comp.comparative_identity_verification_button?.trim().length > 0
  )
  const competitorsAmbas = competidores.filter(
    comp =>
      comp.comparative_electronic_signature_button?.trim().length > 0 &&
      comp.comparative_identity_verification_button?.trim().length > 0
  )

  // Definimos las tabs pasando un prop "type" al CompetitorGrid
  const tabs = [
    {
      title:
        lang === "es"
          ? (pageContext.translates?.firma_electronica || "Firma electrónica")
          : "Electronic signature",
      content: (
        <CompetitorGrid
          type="firma"
          competitors={competitorsFirma}
          lang={lang}
          pathComparativeCompetitors={pathComparativeCompetitors}
        />
      )
    },
    {
      title:
        lang === "es"
          ? (pageContext.translates?.verificacion_de_identidad || "Verificación de identidad")
          : "Identity verification",
      content: (
        <CompetitorGrid
          type="verificacion"
          competitors={competitorsVerificacion}
          lang={lang}
          pathComparativeCompetitors={pathComparativeCompetitors}
        />
      )
    },
    {
      title:
        lang === "es"
          ? (pageContext.translates?.ambas || "Ambas")
          : "Both",
      content: (
        <CompetitorGrid
          type="ambas"
          competitors={competitorsAmbas}
          lang={lang}
          pathComparativeCompetitors={pathComparativeCompetitors}
        />
      )
    }
  ]

  return (
    <NewLayout
      pageContext={pageContext}
      lang={lang}
      translates={pageContext?.translates}
    >
      <SEO
        lang={lang}
        title={page.seo.title}
        description={page.seo.meta_description}
        translates={pageContext.translates}
        image={page.seo.image_2.localFile.publicURL}
      />

      <main className="main">
        <MainSection100 sectionType="white" sectionColor="brown">
          <Container>
            <h1 lang="es" className="merriweather">
              {page.title}
            </h1>
            <div className="container__text">
              <ReactMarkdown
                children={page.description}
                rehypePlugins={[rehypeRaw]}
              />
            </div>
            {/* Render de pestañas */}
            <Tabs tabs={tabs} pills />
          </Container>
        </MainSection100>

        <MainSection100>
          <Container>
            <div className="grid grid-lg-2">
              <Tile
                title={page.customer_stories.name}
                body={page.customer_stories.description}
                image={page.customer_stories.image}
                button={{
                  text: page.customer_stories.button,
                  url: page.customer_stories.url
                }}
              />
              <Tile
                title={page.solicita_demo.name}
                body={page.solicita_demo.description}
                image={page.solicita_demo.image}
                button={{
                  text: page.solicita_demo.button,
                  url: page.solicita_demo.url
                }}
                purple
              />
            </div>
          </Container>
        </MainSection100>
      </main>
    </NewLayout>
  )
}

export default AlternativasCompentenciaPage

export const AlternativasCompentenciaQuery = graphql`
  query ($langKey: String) {
    page: allStrapiAlternativasCompentencia(
      filter: { locale: { eq: $langKey } }
    ) {
      edges {
        node {
          id
          title
          description
          locale
          published_at
          created_at
          updated_at
          seo {
            title
            meta_description
            image_2 {
              alternativeText
              localFile {
                publicURL
                childImageSharp {
                  gatsbyImageData(
                    quality: 100
                    placeholder: BLURRED
                    formats: [WEBP]
                  )
                }
              }
            }
          }
          customer_stories {
            id
            name
            description
            button
            url
            blank
            image {
              alternativeText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 334
                    quality: 100
                    placeholder: BLURRED
                    formats: [WEBP]
                  )
                }
              }
              formats {
                thumbnail {
                  url
                }
              }
            }
          }
          solicita_demo {
            id
            name
            description
            button
            url
            blank
            image {
              alternativeText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 334
                    quality: 100
                    placeholder: BLURRED
                    formats: [WEBP]
                  )
                }
              }
              formats {
                thumbnail {
                  url
                }
              }
            }
          }
          localizations {
            id
            locale
            published_at
          }
        }
      }
    }
    competitives: allStrapiComparativeCompetitors(
      filter: { locale: { eq: $langKey }, menu_active: {eq: true} }
    ) {
      edges {
        node {
          id
          hero_title
          hero_description
          url
          comparative_electronic_signature_button
          comparative_identity_verification_button
          competitor_comparative
          seo {
            meta_description
          }
        }
      }
    }
  }
`
